import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Paper,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import CrearProducto from "../inventory/productos/crearproducto";
import CrearProductoCombo from "../inventory/productos/combo";
import MultiBultoModal from "../inventory/productos/productMultiPack";

const TYPES = [
  {
    id: "convencional",
    title: "Producto convencional",
    desc: "Producto individual con SKU único",
    icon: Inventory2OutlinedIcon,
  },
  {
    id: "combo",
    title: "Combo",
    desc: "Agrupación de productos existentes",
    icon: LayersOutlinedIcon,
  },
  {
    id: "multibulto",
    title: "Multibulto",
    desc: "Producto con múltiples bultos",
    icon: ViewInArOutlinedIcon,
  },
];

// Página de creación de producto en 2 etapas:
//  - Etapa 1 (type === null): selector del tipo de producto. Botón "Cancelar" abajo-izquierda → Inventario.
//  - Etapa 2 (type seteado): formulario del tipo elegido. Botón "Volver" abajo-izquierda → etapa 1.
// La EDICIÓN sigue siendo modal (desde Inventario).
const CreateProduct = () => {
  const navigate = useNavigate();
  const [type, setType] = useState(null);

  const goToInventory = () => navigate("/inventory");
  const goToSelector = () => setType(null);

  const SectionTitle = (
    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        color: "#1a1a1a",
        pl: 1.5,
        borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
        lineHeight: 1.5,
        mb: 3,
      }}
    >
      Nuevo producto
    </Typography>
  );

  // ---------- Etapa 1: selección de tipo ----------
  if (!type) {
    return (
      <Box sx={{ px: { xs: 2, md: 5 }, py: 3 }}>
        {SectionTitle}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <Grid item xs={12} sm={4} key={t.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: "#e0e0e0",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: (theme) => theme.palette.primary.main,
                      boxShadow: (theme) =>
                        `0 2px 10px ${theme.palette.primary.main}20`,
                    },
                  }}
                >
                  <CardActionArea onClick={() => setType(t.id)} sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Icon color="action" />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {t.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.desc}
                        </Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Button
            variant="outlined"
            onClick={goToInventory}
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              borderColor: "#cbd5e1",
              color: "#475569",
              "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
            }}
          >
            Cancelar
          </Button>
        </Box>
      </Box>
    );
  }

  // ---------- Etapa 2: formulario del tipo elegido ----------
  return (
    <Box sx={{ px: { xs: 2, md: 5 }, py: 3 }}>
      {SectionTitle}

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 3,
          border: "1px solid #e0e0e0",
          p: { xs: 2.5, md: 4 },
        }}
      >
        {type === "convencional" && (
          <CrearProducto
            handleCreateProducto={goToInventory}
            productoSKU=""
            handleCloseModal={goToInventory}
            secondaryLabel="Volver"
            onSecondary={goToSelector}
            showBackIcon
          />
        )}
        {type === "combo" && (
          <CrearProductoCombo
            handleCreateProducto={goToInventory}
            productSKU=""
            onClose={goToInventory}
            secondaryLabel="Volver"
            onSecondary={goToSelector}
            showBackIcon
          />
        )}
        {type === "multibulto" && (
          <MultiBultoModal
            handleCreateProducto={goToInventory}
            productSKU=""
            onClose={goToInventory}
            secondaryLabel="Volver"
            onSecondary={goToSelector}
            showBackIcon
          />
        )}
      </Paper>
    </Box>
  );
};

export default CreateProduct;
